import { useState } from 'react';
import { Link } from 'react-router';
import type { TicketLabelAdmin } from '@traceio-org/platform-contracts';
import { PageHeading, PageSection } from '@traceio-org/ui';
import { AdminPage } from '../../components/AdminPage';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { TextField } from '../../components/fields';
import { ScrollTable, StatusBadge } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAbilities, useAdmin } from '../../context';
import { useAdminRead } from '../../useAdminRead';
import { LabelChip } from './TicketList';

/** A few colours to start from (the design's label colours); any lowercase hex works. */
const PRESET_COLORS = [
  { value: '#e5484d', name: 'Red' },
  { value: '#d97706', name: 'Amber' },
  { value: '#0d9488', name: 'Teal' },
  { value: '#2563eb', name: 'Blue' },
  { value: '#8b5cf6', name: 'Violet' },
  { value: '#db2777', name: 'Pink' },
  { value: '#525252', name: 'Grey' },
] as const;

const HEX = /^#[0-9a-f]{6}$/;
const NAME_MAX = 40;

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; label: TicketLabelAdmin }
  | { kind: 'archive'; label: TicketLabelAdmin };

interface Form {
  name: string;
  color: string;
  group: string;
}

type Errors = Partial<Record<keyof Form, string>>;

function validate(form: Form): Errors {
  const errors: Errors = {};
  const name = form.name.trim();
  if (!name) errors.name = 'Name the label.';
  else if (name.length > NAME_MAX) errors.name = `Use at most ${NAME_MAX} characters.`;
  if (!HEX.test(form.color.trim().toLowerCase())) errors.color = 'Use a hex colour such as #3b82f6.';
  if (form.group.trim().length > NAME_MAX) errors.group = `Use at most ${NAME_MAX} characters.`;
  return errors;
}

/**
 * Ticket labels (/tickets/labels): every label with its colour and optional group (Area, Type), live or archived.
 * Staff with staff.tickets.labels.manage create, rename, recolour, regroup, archive and restore them; an archived
 * label stays on the tickets that carry it and is not put on another. Labels are Trace's own: customers never see them.
 */
export function LabelsPage() {
  const { api } = useAdmin();
  const abilities = useAbilities();
  const key = abilities.readTickets ? 'ticket-labels' : null;
  const read = useAdminRead(key, (signal) => api.listTicketLabels(signal));
  const labels = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;
  const dialog = useDialogTarget<Dialog>();
  const [form, setForm] = useState<Form>({ name: '', color: PRESET_COLORS[3].value, group: '' });
  const [errors, setErrors] = useState<Errors>({});
  const [notice, setNotice] = useState<string | null>(null);
  const set = <K extends keyof Form>(field: K, value: Form[K]) => setForm((f) => ({ ...f, [field]: value }));
  const open = (target: Dialog) => {
    setForm(
      target.kind === 'create'
        ? { name: '', color: PRESET_COLORS[3].value, group: '' }
        : { name: target.label.name, color: target.label.color, group: target.label.group ?? '' },
    );
    setErrors({});
    dialog.show(target);
  };
  const target = dialog.target;
  const canManage = abilities.manageTicketLabels;

  let body;
  if (!abilities.readTickets) body = <p className="admin-muted">Your staff role does not include tickets.</p>;
  else if (read.failure)
    body = <AdminProblem failure={read.failure} subject="the ticket labels" onRetry={read.retry} />;
  else if (!labels) body = <LoadingBlock label="Loading the labels" />;
  else if (labels.length === 0) body = <p className="admin-muted">No label yet.</p>;
  else
    body = (
      <ScrollTable caption="Ticket labels" className="admin-table" busy={read.loading}>
        <thead>
          <tr>
            <th scope="col">Label</th>
            <th scope="col">Group</th>
            <th scope="col">Status</th>
            {canManage ? <th scope="col">Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {labels.map((label) => (
            <tr key={label.id}>
              <th scope="row">
                <LabelChip label={label} />
              </th>
              <td>{label.group ?? <span className="admin-muted">None</span>}</td>
              <td>
                <StatusBadge status={label.archivedAt ? 'archived' : 'active'} />
              </td>
              {canManage ? (
                <td className="admin-row-actions">
                  <button
                    type="button"
                    className="admin-action"
                    aria-label={`Edit ${label.name}`}
                    onClick={() => open({ kind: 'edit', label })}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-action"
                    aria-label={`${label.archivedAt ? 'Restore' : 'Archive'} ${label.name}`}
                    onClick={() => open({ kind: 'archive', label })}
                  >
                    {label.archivedAt ? 'Restore' : 'Archive'}
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    );

  const archiving = target?.kind === 'archive' ? target.label : null;
  return (
    <AdminPage title="Ticket labels">
      <Link className="admin-back" to="/tickets">
        <span aria-hidden="true">&larr;</span> Tickets
      </Link>
      <PageHeading
        title="Ticket labels"
        description="Labels sort Trace's queue, for example by area and type. Customers never see them."
      />
      <SavedNotice message={notice} />
      <PageSection
        title="Labels"
        action={
          canManage && labels ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => open({ kind: 'create' })}
            >
              New label
            </button>
          ) : undefined
        }
      >
        {body}
      </PageSection>
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={
          archiving
            ? `${archiving.archivedAt ? 'Restore' : 'Archive'} ${archiving.name}?`
            : target?.kind === 'edit'
              ? `Edit ${target.label.name}`
              : 'New label'
        }
        description={
          archiving
            ? archiving.archivedAt
              ? 'It can be put on tickets again.'
              : 'It stays on the tickets that carry it, and is no longer offered for others.'
            : undefined
        }
        submitLabel={
          archiving
            ? archiving.archivedAt
              ? 'Restore'
              : 'Archive'
            : target?.kind === 'edit'
              ? 'Save'
              : 'Create label'
        }
        tone={archiving && !archiving.archivedAt ? 'danger' : 'default'}
        onSubmit={async () => {
          if (archiving) {
            await api.updateTicketLabel(archiving.id, { archived: !archiving.archivedAt });
            return undefined;
          }
          const found = validate(form);
          setErrors(found);
          if (Object.keys(found).length > 0) return 'invalid';
          const color = form.color.trim().toLowerCase();
          const group = form.group.trim() || null;
          if (target?.kind === 'edit') {
            await api.updateTicketLabel(target.label.id, { name: form.name.trim(), color, group });
          } else {
            await api.createTicketLabel({ name: form.name, color, group });
          }
          return undefined;
        }}
        onSaved={() => {
          setNotice(
            archiving
              ? `${archiving.name} is ${archiving.archivedAt ? 'restored' : 'archived'}.`
              : target?.kind === 'edit'
                ? `${form.name.trim()}: saved.`
                : `${form.name.trim()} is created.`,
          );
          read.retry();
        }}
      >
        {archiving ? null : (
          <>
            <TextField
              label="Name"
              value={form.name}
              onChange={(v) => set('name', v)}
              error={errors.name ?? null}
              maxLength={NAME_MAX}
              required
            />
            <TextField
              label="Colour"
              value={form.color}
              onChange={(v) => set('color', v)}
              error={errors.color ?? null}
              hint={
                <span className="admin-swatches" role="group" aria-label="Preset colours">
                  {PRESET_COLORS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      className="admin-swatch-button"
                      aria-label={preset.name}
                      aria-pressed={form.color.toLowerCase() === preset.value}
                      style={{ background: preset.value }}
                      onClick={() => set('color', preset.value)}
                    />
                  ))}
                </span>
              }
              spellCheck={false}
              required
            />
            <TextField
              label="Group"
              value={form.group}
              onChange={(v) => set('group', v)}
              error={errors.group ?? null}
              hint="Optional, for example Area or Type."
              maxLength={NAME_MAX}
            />
          </>
        )}
      </WriteDialog>
    </AdminPage>
  );
}
